import { airBrandMap } from '../constants/BrandMaps';
import { formatCurrency, isBXcurrency } from '../utils/currency.utils';
import i18n from '../i18n';
import moment from 'moment';
import { generate_uuidv4 } from './common.utils';
import { GdsSource, MULTIPLE_AIRLINE } from '../constants/Enums';

export const ALL = 'ALL';
export const MORNING = 'MORNING';
export const MIDDAY = 'MIDDAY';
export const EVENING = 'EVENING';

const times = {
  [ALL]: {
    label: 'All',
    from: 0,
    to: 2359,
  },
  [MORNING]: {
    label: 'Morning',
    from: 0,
    to: 1059,
  },
  [MIDDAY]: {
    label: 'Midday',
    from: 1100,
    to: 1659,
  },
  [EVENING]: {
    label: 'Evening',
    from: 1700,
    to: 2359,
  },
};

export const convertAirDataToSegment = (data, currency, currencyCulture) => {
  if (!data) {
    return data;
  }

  const result = data.reduce(
    (acum, { AirLegs, MultiBrands, selectedBrand }) => {
      const depart = AirLegs[0].FilterDepart;
      //We set the payment mode here
      const Total = isBXcurrency(currencyCulture)
        ? MultiBrands[selectedBrand].TotalPoints
        : MultiBrands[selectedBrand].TotalFare;
      if (depart >= times.MORNING.from && depart <= times.MORNING.to) {
        acum[MORNING] = acum[MORNING] > Total ? Total : acum[MORNING];
      } else if (depart >= times.MIDDAY.from && depart <= times.MIDDAY.to) {
        acum[MIDDAY] = acum[MIDDAY] > Total ? Total : acum[MIDDAY];
      } else if (depart >= times.EVENING.from && depart <= times.EVENING.to) {
        acum[EVENING] = acum[EVENING] > Total ? Total : acum[EVENING];
      }

      return acum;
    },
    {
      [ALL]: Infinity,
      [MORNING]: Infinity,
      [MIDDAY]: Infinity,
      [EVENING]: Infinity,
    }
  );

  const minPrice = Math.min(...Object.values(result));
  result[ALL] = isNaN(minPrice) ? Infinity : minPrice;

  return Object.entries(result).map(([key, value]) => ({
    label:
      value === Infinity
        ? 'None'
        : formatCurrency(value, isBXcurrency(currencyCulture) ? '' : currency, currencyCulture),
    value: key,
    second: times[key].label,
  }));
};

export const convertAirDataToSegmentCloud = (data, currency, currencyCulture) => {
  if (!data) {
    return data;
  }

  const result = data.reduce(
    (acum, curr) => {
      const depart = parseInt(
        moment(curr?.legs[0]?.flights[0]?.departureTime, 'hh:mm').format('HHmm'),
        10
      );
      const Total = curr.fares[0]?.total;
      if (depart >= times.MORNING.from && depart <= times.MORNING.to) {
        acum[MORNING] = acum[MORNING] > Total ? Total : acum[MORNING];
      } else if (depart >= times.MIDDAY.from && depart <= times.MIDDAY.to) {
        acum[MIDDAY] = acum[MIDDAY] > Total ? Total : acum[MIDDAY];
      } else if (depart >= times.EVENING.from && depart <= times.EVENING.to) {
        acum[EVENING] = acum[EVENING] > Total ? Total : acum[EVENING];
      }

      return acum;
    },
    {
      [ALL]: Infinity,
      [MORNING]: Infinity,
      [MIDDAY]: Infinity,
      [EVENING]: Infinity,
    }
  );
  const minPrice = Math.min(...Object.values(result));
  result[ALL] = isNaN(minPrice) ? Infinity : minPrice;

  return Object.entries(result).map(([key, value]) => ({
    label:
      value === Infinity
        ? 'None'
        : formatCurrency(value, isBXcurrency(currencyCulture) ? '' : currency, currencyCulture),
    value: key,
    second: times[key].label,
  }));
};

export const filterByMatrix = (list, filterBy) => {
  if (!list || !filterBy || filterBy === ALL) {
    return list;
  }

  return list.filter(
    ({ AirLegs }) =>
      AirLegs[0].FilterDepart >= times[filterBy].from &&
      AirLegs[0].FilterDepart <= times[filterBy].to
  );
};

export const filterByMatrixCloud = (list, filterBy) => {
  if (!list || !filterBy || filterBy === ALL) {
    return list;
  }

  return list.filter((item) => {
    const depart = parseInt(
      moment(item?.legs[0]?.flights[0]?.departureTime, 'hh:mm').format('HHmm'),
      10
    );
    return depart >= times[filterBy].from && depart <= times[filterBy].to;
  });
};

export const isAirResultDisabled = (result) => {
  return (
    result.IsSuppressAirline ||
    (result?.MultiBrands &&
      (result.MultiBrands[0]?.IsCabinGrayOut || result.MultiBrands[0]?.IsSuppressed))
  );
};

export function isAirResultDisabledCloud(result) {
  return result?.isSuppressAirline || result?.fares[0]?.isSuppressed;
}

export const isAirBrandEnabled = (brand) => {
  return brand.isSelectable && !brand.IsCabinGreyOut;
};

const getMinPricesByBrand = (list) => {
  return list
    .filter((i) => i.MultiBrands)
    .reduce((arr, curr) => {
      return curr.MultiBrands.reduce((acum, brand) => {
        const { LegBrandName, TotalFare, TotalPoints } = brand;
        const brandName = LegBrandName.toLowerCase();
        const totalPoints = parseInt(TotalPoints);
        const total = TotalFare || totalPoints;
        acum[brandName] = acum[brandName] && acum[brandName] < total ? acum[brandName] : total;

        //this condition is for checking if we are in BX flow
        if (totalPoints > 0) {
          return acum;
        }

        if (acum.lowest) {
          acum.lowest = acum.lowest < total ? acum.lowest : total;
        } else {
          acum.lowest = total;
        }
        return acum;
      }, arr);
    }, {});
};

const getMinPricesByBrandCloud = (list) => {
  return list
    .filter((i) => i.fares)
    ?.reduce((arr, curr) => {
      return curr?.fares?.reduce((acum, brand) => {
        const brandName = brand.brand.toLowerCase();
        acum[brandName] =
          acum[brandName] && acum[brandName] < brand.total ? acum[brandName] : brand.total;

        if (acum.lowest) {
          acum.lowest = acum.lowest < brand.total ? acum.lowest : brand.total;
        } else {
          acum.lowest = brand.total;
        }
        return acum;
      }, arr);
    }, {});
};

export const getBrandsAvailable = (list) => {
  if (!list) {
    return list;
  }

  const minPricesByBrand = getMinPricesByBrand(list);
  return Object.keys(airBrandMap)
    .filter((b) => minPricesByBrand[b])
    .map((key) => ({
      ...airBrandMap[key],
      brand: key,
      price: minPricesByBrand[key],
    }));
};
export const getBrandsAvailableCloud = (list) => {
  if (!list) {
    return list;
  }

  const minPricesByBrand = getMinPricesByBrandCloud(list);
  return Object.keys(airBrandMap)
    .filter((b) => minPricesByBrand[b])
    .map((key) => ({
      ...airBrandMap[key],
      brand: key,
      price: minPricesByBrand[key],
    }));
};

export const mapAirportList = (list) => {
  if (!list) {
    return list;
  }

  return list.reduce((acum, value) => {
    acum[value.airportCode] = {
      name: value.airportName,
      price: value.airportLowestPrice,
      points: value.points,
      checked: true,
    };

    return acum;
  }, {});
};

export const getFiltersFlight = (list) => {
  if (!list) {
    return list;
  }

  return list.reduce((res, item) => {
    item.AirLegs.forEach(({ AirSegments, AirlineCode }) => {
      const length = AirSegments.length > 2 ? 3 : AirSegments.length;

      if (res[length]) {
        res[length].count++;
        if (res[length].lowestPrice > item.TotalAmount) {
          res[length].lowestPrice = item.TotalAmount;
        }
        if (res[length].TotalPoints > item.TotalPoints) {
          res[length].lowestPoints = item.lowestPoints;
        }
      } else {
        res[length] = {
          count: 1,
          lowestPrice: item.TotalAmount,
          lowestPoints: item.TotalPoints,
          items: {},
          checked: true,
        };
      }

      if (res[length].items[AirlineCode]) {
        const existent = res[length].items[AirlineCode];
        if (existent.price > item.TotalAmount) {
          existent.price = item.TotalAmount;
        }
        if (existent.points > item.TotalPoints) {
          existent.points = item.TotalPoints;
        }
        existent.count++;
      } else {
        res[length].items[AirlineCode] = {
          name:
            item.AirlineName === 'Multiple Airline'
              ? i18n.t('common:AirFilter.MultipleAirlines')
              : item.AirlineName,
          price: item.TotalAmount,
          points: item.TotalPoints,
          count: 1,
          checked: true,
        };
      }
    });

    return res;
  }, {});
};

export const getFiltersFlightCloud = (list) => {
  if (!list) {
    return list;
  }

  return list?.reduce((res, item) => {
    item?.legs?.forEach((leg) => {
      const length = leg?.flights?.length > 2 ? 3 : leg?.flights?.length;
      const airlineCode = leg?.isMultipleAirline ? MULTIPLE_AIRLINE : leg?.flights[0]?.marketting;
      const fareTotal = item.fares[0]?.total ?? item.fares[1]?.total;
      if (res[length]) {
        res[length].count++;
        if (res[length].lowestPrice > fareTotal) {
          res[length].lowestPrice = fareTotal;
        }
      } else {
        res[length] = {
          count: 1,
          lowestPrice: fareTotal,
          lowestPoints: 0,
          items: {},
          checked: true,
        };
      }

      if (res[length].items[airlineCode]) {
        const existent = res[length].items[airlineCode];
        if (existent.price > fareTotal) {
          existent.price = fareTotal;
        }
        existent.count++;
      } else {
        res[length].items[airlineCode] = {
          name: leg?.isMultipleAirline
            ? i18n.t('common:AirFilter.MultipleAirlines')
            : leg?.flights.filter((flight) => flight.marketting === airlineCode)[0]?.marketting,
          price: fareTotal,
          points: 0,
          count: 1,
          checked: true,
        };
      }
    });
    return res;
  }, {});
};

export const getAirFiltersByDetails = (airResponses, departureAirports, arrivalAirportList) => {
  if (!airResponses) {
    return airResponses;
  }

  const filterByDetails = {
    stopAndVendor: getFiltersFlight(airResponses),
  };

  if (
    (departureAirports && Object.keys(departureAirports).length > 1) ||
    (arrivalAirportList && Object.keys(arrivalAirportList).length > 1)
  ) {
    const airports = airResponses.reduce(
      (acum, { AirLegs, TotalFare }) => {
        const airSegments = AirLegs[0].AirSegments;
        const departure = airSegments[0].DepartureAirportCode;
        const arrival = airSegments[airSegments.length - 1].ArrivalAirportCode;

        if (!acum.dep[departure] || acum.dep[departure] > TotalFare) {
          acum.dep[departure] = TotalFare;
        }

        if (!acum.arr[arrival] || acum.arr[departure] > TotalFare) {
          acum.arr[arrival] = TotalFare;
        }

        return acum;
      },
      {
        dep: {},
        arr: {},
      }
    );

    if (Object.keys(airports.dep).length > 1) {
      filterByDetails.departureAirport = {
        departure: {
          items: Object.entries(airports.dep).reduce((acum, [code, price]) => {
            acum[code] = departureAirports[code];
            acum[code].price = price;
            return acum;
          }, {}),
        },
      };
    }

    if (Object.keys(airports.dep).length > 1) {
      filterByDetails.arrivalAirport = {
        arrival: {
          items: Object.entries(airports.arr).reduce((acum, [code, price]) => {
            acum[code] = arrivalAirportList[code];
            acum[code].price = price;
            return acum;
          }, {}),
        },
      };
    }
  }

  return filterByDetails;
};

export const getAirFiltersByDetailsCloud = (
  airResponses,
  departureAirports,
  arrivalAirportList
) => {
  if (!airResponses) {
    return airResponses;
  }

  const filterByDetails = {
    stopAndVendor: getFiltersFlightCloud(airResponses),
  };

  if (
    (departureAirports && Object.keys(departureAirports).length > 1) ||
    (arrivalAirportList && Object.keys(arrivalAirportList).length > 1)
  ) {
    const airports = airResponses?.reduce(
      (acum, item) => {
        const departure = item?.legs[0]?.flights[0]?.departure;
        const arrival = item?.legs[0]?.flights[0]?.arrival;
        const fareTotal = item.fares[0]?.total ?? item.fares[1]?.total;
        if (!acum.dep[departure] || acum.dep[departure] > fareTotal) {
          acum.dep[departure] = fareTotal;
        }
        if (!acum.arr[arrival] || acum.arr[arrival] > fareTotal) {
          acum.arr[arrival] = fareTotal;
        }
        return acum;
      },
      {
        dep: {},
        arr: {},
      }
    );

    if (Object.keys(airports.dep).length > 1) {
      filterByDetails.departureAirport = {
        departure: {
          items: Object.entries(airports.dep).reduce((acum, [code, price]) => {
            acum[code] = departureAirports[code];
            acum[code].price = price;
            return acum;
          }, {}),
        },
      };
    }

    if (Object.keys(airports.dep).length > 1) {
      filterByDetails.arrivalAirport = {
        arrival: {
          items: Object.entries(airports.arr).reduce((acum, [code, price]) => {
            acum[code] = arrivalAirportList[code];
            acum[code].price = price;
            return acum;
          }, {}),
        },
      };
    }
  }

  return filterByDetails;
};

export const getAirFilterByAlternateAirports = (airResponses) => {
  const filterByAlternateAirports = airResponses.filter(
    (flights) => !flights?.isLowestAlternateAirportResponse
  );
  return filterByAlternateAirports;
};

function getNdcOfferItemId(fareDetails, dataSource) {
  if (dataSource == GdsSource.NDC) {
    let offerItemIds = '';
    if (fareDetails !== undefined && fareDetails.length) {
      fareDetails.map((data) => (offerItemIds += data.type + '/' + data.offerItemId + ','));
      return offerItemIds.slice(0, -1);
    }
    return offerItemIds;
  } else if (dataSource == GdsSource.SabreNDC) {
    const ndcOfferIds = [];
    if (fareDetails !== undefined && fareDetails.length) {
      fareDetails.map((data) => ndcOfferIds.push(data.offerItemId));
    }
    return ndcOfferIds.toString();
  }
}

function buildNDCRequest(airResponse, selectedBrand, repriceData) {
  if (repriceData && Object.values(repriceData).length) {
    airResponse.NdcRepriceOffId = repriceData.NdcRepriceOffId;
    airResponse.NdcRepriceResId = repriceData.NdcRepriceResId;
  }
  airResponse.NdcResId = selectedBrand.shopResId;
  airResponse.NdcOffId =
    selectedBrand.gdsKey == GdsSource.SabreNDC || selectedBrand.gdsKey == GdsSource.NDC
      ? getNdcOfferItemId(selectedBrand?.price, selectedBrand?.gdsKey)
      : '';
  airResponse.NdcOffItemId =
    selectedBrand.gdsKey == GdsSource.SabreNDC || selectedBrand.gdsKey == GdsSource.NDC
      ? getNdcOfferItemId(selectedBrand?.price, selectedBrand?.gdsKey)
      : '';
}

export const buildAirSegment = (
  searchInput,
  airLegs,
  airResponse,
  selectedBrand,
  legNumber,
  airports = null
) => {
  const airSegmentList = [];
  for (let airLeg = 0; airLeg < airLegs.length; airLeg++) {
    const flights = airLegs[airLeg].flights;

    for (let i = 0; i < flights.length; i++) {
      const airSegment = {};
      airSegment.GUID = generate_uuidv4();
      airSegment.LegNumber = legNumber;
      airSegment.MarketingAirline = {};
      airSegment.MarketingAirline.Code = flights[i].operating;
      airSegment.MarketingAirline.Name = flights[i].operating;

      airSegment.OperatingAirline = {};
      airSegment.OperatingAirline.Name = flights[i].operating;
      airSegment.OperatingAirline.Code = flights[i].operating;
      airSegment.OperatingAirline.CompanyShortName = flights[i].operatingShortName;

      airSegment.ResBookDesigCode = selectedBrand.resCodes[i] ?? '';

      airSegment.OperatingflightNumber = flights[i].operatingNumber;
      airSegment.SeatRemaining = 0;
      airSegment.FlightNumber = flights[i].operatingNumber;
      airSegment.Duration = flights[i].totalDuration;

      airSegment.DepartureDate = `${flights[i].departureDate} ${flights[i].departureTime}`;
      airSegment.ArrivalDate = `${flights[i].arrivalDate} ${flights[i].arrivalTime}`;
      airSegment.MarriageGrp = selectedBrand.marriageGrp[i] ?? '';
      airSegment.SegmentOrder = i + 1;
      airSegment.BrandName = selectedBrand?.segBrandsNames[i] ?? '';
      airSegment.Cabin = selectedBrand?.segCabin[i] ?? '';
      airSegment.OriginCabinFromSearch = selectedBrand?.segCabin[i] ?? '';

      airSegment.DepartureAirport = {};
      airSegment.DepartureAirport.Code = flights[i].departure;
      airSegment.DepartureAirport.Name = searchInput.searchCriteria[0].departureAirport ?? '';
      const DepartureAirportCityName = airports?.find(
        (airport) => airport.code === flights[i].departure
      )?.CityName;
      airSegment.DepartureAirport.Address = { City: DepartureAirportCityName };

      airSegment.ArrivalAirport = {};
      airSegment.ArrivalAirport.Code = flights[i].arrival;
      airSegment.ArrivalAirport.Name = searchInput.searchCriteria[0].arrivalAirport ?? '';
      const ArrivalAirportCityName = airports?.find(
        (airport) => airport.code === flights[i].arrival
      )?.CityName;
      airSegment.ArrivalAirport.Address = { City: ArrivalAirportCityName };

      airSegment.FareBasisCode = selectedBrand.farebasisCode[i];

      airSegment.BrandID = selectedBrand?.segBrandCode[i] ?? ''; //selectedBrand.id;
      airSegmentList.push(airSegment);
    }
    airResponse.AirLegs = legNumber > 1 ? airResponse.AirLegs : [];
    airResponse.AirLegs.push({
      AirSegments: airSegmentList,
      HaulType: 'SH',
      AirLegBrandName: selectedBrand.brand,
      ContractCode: selectedBrand.contractCode[0],
    });
  }
};
export const buildAirTravelComponentResponse = async (
  selectedAir,
  searchInput,
  airports = null,
  ndcData = null,
  repriceData = null
) => {
  try {
    const airResponse = {};
    if (searchInput.hasAir) {
      const selectedFlights = Object.keys(selectedAir).map(
        (key) => selectedAir[key]['selectedAir']
      );
      const selectedFareIDList = Object.keys(selectedAir).map(
        (key) => selectedAir[key]['newMultiBrandResponseKey']
      );
      for (let i = 0; i < selectedFlights.length; i++) {
        if (typeof selectedFlights[i] !== 'undefined') {
          const selectedBrand = selectedFlights[i].fares.filter(
            (fare) => fare.id === selectedFareIDList[i]
          )[0];
          airResponse.Id = generate_uuidv4(); // selectedFlights[i].id;
          airResponse.BaseCost = selectedBrand.baseCost;
          airResponse.TaxCost = selectedBrand.tax;
          airResponse.TotalBaseCost = selectedBrand.baseTotal;
          airResponse.TotalTaxCost = selectedBrand.taxTotal;

          airResponse.AgentWareQueryID = '';
          airResponse.AgentWareItineraryID = '';

          airResponse.CurrencyRate = searchInput.CurrencyRate ?? 1;
          airResponse.SourceID = parseInt(selectedBrand.gdsKey);

          airResponse.ITAQueryId = '';
          airResponse.ITAItineraryId = '';

          airResponse.IsRefundable = selectedBrand.IsRefundable;
          airResponse.MappedResponseId = selectedFlights[i].id;
          airResponse.ValidatingCarrier = selectedFlights[i].legs[0].validatingCarrier ?? '';

          airResponse.ContractCode = selectedBrand.contractCode[0] ?? '';

          const isNdc =
            selectedBrand.gdsKey == GdsSource.SabreNDC || selectedBrand.gdsKey == GdsSource.NDC;
          if (isNdc) {
            buildNDCRequest(airResponse, selectedBrand, ndcData);
          } else {
            assignRepriceValuesToAirRes(airResponse, repriceData);
          }

          buildAirSegment(
            searchInput,
            selectedFlights[i].legs,
            airResponse,
            selectedBrand,
            i + 1,
            airports
          );
        }
      }
    }
    __DEV__ && console.log(airResponse);
    return airResponse;
  } catch (err) {
    console.error(err);
  }
};

export function removeDuplicatesFromArray(a, key) {
  const seen = new Set();
  return a.filter((item) => {
    const k = key(item);
    return seen.has(k) ? false : seen.add(k);
  });
}

function assignRepriceValuesToAirRes(airResponse, repriceData) {
  if (repriceData && Object.values(repriceData) && Object.values(repriceData).length) {
    if (
      airResponse.baseCost === repriceData.TotalBaseFare &&
      airResponse.TaxCost === repriceData.TotalTax
    ) {
      return;
    }
    airResponse.BaseCost = repriceData.TotalBaseFare;
    airResponse.TaxCost = repriceData.TotalTax;
    airResponse.TotalBaseCost = repriceData.TotalBaseFare;
    airResponse.TotalTaxCost = repriceData.TotalTax;
  }
}
